import { faDigits } from "./connection.js";

/**
 * Display formats shared by every screen (AGENTS.md §4: Asia/Tehran, Jalali, Persian digits; §3.6 money in rial).
 * Money is stored as integer rial and shown in toman by default (AS-030); inputs in toman are converted exactly.
 */

const jalali = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
  timeZone: "Asia/Tehran",
  year: "numeric",
  month: "long",
  day: "numeric",
});
const jalaliShort = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
  timeZone: "Asia/Tehran",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const weekday = new Intl.DateTimeFormat("fa-IR", { timeZone: "Asia/Tehran", weekday: "long" });

/** A business date (ISO `YYYY-MM-DD`) at Tehran noon, so the calendar day never shifts. */
const noon = (iso: string) => new Date(`${iso}T08:30:00Z`);

/** «۸ آذر ۱۴۰۵» */
export function formatDate(iso: string): string {
  return faDigits(jalali.format(noon(iso)));
}

/** «۱۴۰۵/۰۹/۰۸» */
export function formatDateShort(iso: string): string {
  return faDigits(jalaliShort.format(noon(iso)));
}

/** «شنبه» */
export function formatWeekday(iso: string): string {
  return weekday.format(noon(iso));
}

/** Tehran wall-clock time of an instant: «۰۹:۳۰» */
export function formatTime(instant: string | Date): string {
  const d = typeof instant === "string" ? new Date(instant) : instant;
  const t = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tehran",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);
  return faDigits(t);
}

export type MoneyUnit = "toman" | "rial";

/** 45_000_000 rial → «۴٬۵۰۰٬۰۰۰ تومان». Never rounds: 1 toman = 10 rial, and stored rial is a multiple of 10. */
export function formatMoney(rial: number, unit: MoneyUnit = "toman"): string {
  const value = unit === "toman" ? Math.trunc(rial / 10) : rial;
  const grouped = String(Math.abs(value)).replace(/\B(?=(\d{3})+(?!\d))/g, "٬");
  return `${value < 0 ? "−" : ""}${faDigits(grouped)} ${unit === "toman" ? "تومان" : "ریال"}`;
}

/**
 * Parses what a person typed (Persian, Arabic or Latin digits, with or without separators) in `unit` and
 * returns integer rial, or null when it is not a whole non-negative amount.
 */
export function parseMoney(input: string, unit: MoneyUnit = "toman"): number | null {
  const ascii = input
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[\s,٬،]/g, "");
  if (!/^\d{1,15}$/.test(ascii)) return null;
  const n = Number(ascii);
  return unit === "toman" ? n * 10 : n;
}
